import React, { useState } from 'react';
import { Search, X, Printer, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

export const PatientPortal = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const handleSearch = async () => {
    setError('');
    setLoading(true);
    
    // Validate 10-digit mobile number
    if (!/^\d{10}$/.test(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      setLoading(false);
      return;
    }

    try {
      // Query Firestore for bills with matching mobile number
      const billsRef = collection(db, 'patient_bills');
      const q = query(billsRef, where('mobile', '==', mobileNumber));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('No bills found for this mobile number');
        setLoading(false);
        return;
      }

      // Get the most recent bill
      const bills = [];
      querySnapshot.forEach((doc) => {
        bills.push({ id: doc.id, ...doc.data() });
      });

      // Sort by createdAt descending and get the latest
      bills.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });

      const latestBill = bills[0];
      setReceiptData(latestBill);
      setShowReceipt(true);
    } catch (error) {
      console.error('Error fetching bill:', error);
      setError('Failed to fetch bill. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <section
      id="portal"
      data-testid="patient-portal-section"
      className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="container mx-auto max-w-2xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4">
            <span className="text-gradient-primary">View Lab Bill</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
            Enter your mobile number to view and download your lab test bill
          </p>
        </div>

        {/* Search Card */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <div className="space-y-6">
            <div>
              <Label htmlFor="mobile" className="text-slate-700 font-medium mb-3 block text-base">
                Mobile Number
              </Label>
              <Input
                id="mobile"
                type="tel"
                data-testid="patient-portal-mobile-input"
                placeholder="Enter 10-digit mobile number"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setError('');
                }}
                maxLength={10}
                className="text-lg py-6 border-slate-300 rounded-xl"
              />
              {error && (
                <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <Button
              data-testid="patient-portal-search-button"
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#0284C7] to-[#059669] text-white hover:shadow-xl py-6 text-base font-semibold transition-all duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Search Bill
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {receiptData && (
        <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
          <DialogContent
            data-testid="patient-receipt-modal"
            className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
          >
            <div id="receipt-print-area" className="bg-white">
              {/* Receipt Header */}
              <div className="bg-gradient-to-r from-slate-900 via-sky-950 to-slate-900 text-white p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold font-['Outfit']">USHAKIRAN MEDICOS</h2>
                    <p className="text-sm text-slate-300 mt-1">Polyclinic & Path Lab (Estd. 2009)</p>
                  </div>
                  <button
                    data-testid="patient-receipt-close-button"
                    onClick={() => setShowReceipt(false)}
                    className="print:hidden text-white hover:bg-white/20 p-2 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="border-t border-slate-700 pt-4">
                  <p className="text-sm">Contact: 033-24314431 / 9007667528</p>
                </div>
              </div>

              {/* Receipt Body */}
              <div className="p-6 sm:p-8 space-y-6">
                {/* Invoice Info */}
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Invoice ID</p>
                    <p className="text-lg font-semibold text-slate-900">{receiptData.invoiceId}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="text-lg font-semibold text-slate-900">{receiptData.date}</p>
                  </div>
                </div>

                {/* Patient Details */}
                <div className="bg-slate-50 rounded-xl p-6 space-y-3">
                  <h3 className="font-semibold text-slate-900 mb-4">Patient Details</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p data-testid="patient-receipt-patient-name" className="font-medium text-slate-900">
                        {receiptData.patientName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Mobile</p>
                      <p data-testid="patient-receipt-mobile-number" className="font-medium text-slate-900">
                        {receiptData.mobile}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Age</p>
                      <p className="font-medium text-slate-900">{receiptData.age} years</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Gender</p>
                      <p className="font-medium text-slate-900">{receiptData.gender}</p>
                    </div>
                  </div>
                </div>

                {/* Tests List */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Tests Performed</h3>
                  <div className="space-y-3">
                    {receiptData.tests.map((test, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center py-3 border-b border-slate-200 last:border-0"
                      >
                        <span className="text-slate-700">{test.name}</span>
                        <span className="font-semibold text-slate-900">₹{test.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Amount Summary */}
                <div className="bg-gradient-to-br from-sky-50 to-emerald-50 rounded-xl p-6 space-y-3">
                  <div className="flex justify-between items-center text-lg">
                    <span className="text-slate-700">Total Amount</span>
                    <span data-testid="patient-receipt-total-amount" className="font-bold text-slate-900">
                      ₹{receiptData.totalAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Paid Amount</span>
                    <span data-testid="patient-receipt-paid-amount" className="font-semibold text-emerald-600">
                      ₹{receiptData.paidAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-slate-300">
                    <span className="text-slate-700 font-semibold">Due Amount</span>
                    <span data-testid="patient-receipt-due-amount" className="font-bold text-red-600 text-xl">
                      ₹{receiptData.dueAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 text-sm">Payment Mode</span>
                    <Badge className="bg-sky-100 text-sky-700">
                      {receiptData.paymentMode}
                    </Badge>
                  </div>
                  <div className="flex justify-end">
                    <Badge
                      className={`${
                        receiptData.paymentStatus === 'Paid'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {receiptData.paymentStatus === 'Paid' ? (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mr-1" />
                      )}
                      {receiptData.paymentStatus}
                    </Badge>
                  </div>
                </div>

                {/* Print Button */}
                <Button
                  data-testid="patient-receipt-print-button"
                  onClick={handlePrint}
                  className="w-full bg-gradient-to-r from-slate-900 to-slate-700 text-white hover:shadow-xl py-6 text-base font-semibold transition-all duration-300 print:hidden"
                >
                  <Printer className="w-5 h-5 mr-2" />
                  Print Receipt
                </Button>

                {/* Footer Note */}
                <div className="text-center text-sm text-slate-500 pt-4 border-t border-slate-200">
                  <p>Thank you for choosing USHAKIRAN MEDICOS</p>
                  <p className="mt-1">For queries: 033-24314431 / 9007667528</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};